// Removes every CSS-selector field from a Datadog RUM intake batch.
//
// The browser SDK builds selectors from the interacted element's test ids, ids,
// classes and sibling position, and they can encode what a user chose. The SDK's
// `beforeSend` cannot remove them (it copies back only an allow-list of fields),
// so they are removed here, server-side, by key name: any key that is exactly
// `selector` or ends in `_selector`, at any depth. Matching by name rather than
// by an enumerated path list also covers selector fields a later SDK adds.
//
// Fail closed: a line is forwarded only after it was parsed and re-serialised.
// Anything that is not a JSON object, or whose kept content nests deeper than
// MAX_EVENT_DEPTH, is dropped rather than passed through. The bound applies to
// what is kept: a subtree under a selector key is deleted without being walked,
// so depth inside it never counts.

export const MAX_EVENT_DEPTH = 64;

export interface StrippedBatch {
  /** Re-serialised, selector-free events, in input order. */
  readonly lines: readonly string[];
  /** Non-blank input lines that were not valid events and were dropped. */
  readonly dropped: number;
  /** Selector keys removed across the batch. */
  readonly removed: number;
}

type JsonContainer = Record<string, unknown> | unknown[];

function isSelectorKey(key: string): boolean {
  return key === 'selector' || key.endsWith('_selector');
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isContainer(value: unknown): value is JsonContainer {
  return typeof value === 'object' && value !== null;
}

// Iterative walk with an explicit stack, so a hostile deeply nested line cannot
// overflow the call stack. Returns the number of keys removed, or `undefined`
// when the value nests deeper than the bound.
function stripInPlace(root: Record<string, unknown>): number | undefined {
  let removed = 0;
  const stack: Array<{ node: JsonContainer; depth: number }> = [{ node: root, depth: 1 }];
  while (stack.length > 0) {
    const frame = stack.pop();
    if (frame === undefined) break;
    const { node, depth } = frame;
    if (depth > MAX_EVENT_DEPTH) return undefined;
    if (Array.isArray(node)) {
      for (const item of node) {
        if (isContainer(item)) stack.push({ node: item, depth: depth + 1 });
      }
      continue;
    }
    for (const key of Object.keys(node)) {
      if (isSelectorKey(key)) {
        delete node[key];
        removed += 1;
        continue;
      }
      const child = node[key];
      if (isContainer(child)) stack.push({ node: child, depth: depth + 1 });
    }
  }
  return removed;
}

function parseEvent(line: string): Record<string, unknown> | undefined {
  try {
    const value: unknown = JSON.parse(line);
    return isPlainObject(value) ? value : undefined;
  } catch {
    // JSON.parse is itself recursive; a pathological line can exhaust the
    // stack here too. Either way the line is not a valid event.
    return undefined;
  }
}

export function stripSelectorsFromBatch(ndjson: string): StrippedBatch {
  const lines: string[] = [];
  let dropped = 0;
  let removed = 0;
  for (const raw of ndjson.split('\n')) {
    if (raw.trim() === '') continue;
    const event = parseEvent(raw);
    const count = event === undefined ? undefined : stripInPlace(event);
    if (event === undefined || count === undefined) {
      dropped += 1;
      continue;
    }
    removed += count;
    lines.push(JSON.stringify(event));
  }
  return { lines, dropped, removed };
}
